import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App';
import { registerServiceWorkerSafely, activateServiceWorker } from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';

// ===== Platform Detection Functions =====

// Check if we're running in standalone mode (PWA)
const isInStandaloneMode = () => 
  window.matchMedia('(display-mode: standalone)').matches || 
  window.navigator.standalone === true;

// Detect if running in iOS simulator
const isIOSSimulator = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for standard simulator strings
  if (ua.includes('iPhone Simulator') || ua.includes('iPad Simulator')) {
    return true;
  }
  
  // Additional indicators that might suggest simulator
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isLocalhostOrNonStandard = window.location.hostname === 'localhost' || 
    window.location.hostname.includes('local') ||
    window.location.port === '3000' || 
    window.location.port === '8080';
  
  // In simulator, these features often behave differently 
  const hasNonStandardBehavior = isIOS && isLocalhostOrNonStandard;
  
  return hasNonStandardBehavior;
};

// Detect iOS version
const getIOSVersion = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  
  if (!isIOS) return null;
  
  const match = ua.match(/OS (\d+)_?(\d+)?/);
  if (match) {
    return {
      major: parseInt(match[1], 10),
      minor: match[2] ? parseInt(match[2], 10) : 0
    };
  }
  
  return null;
};

// ===== Application Initialization =====

// REMOVED: Firebase initialization from here - now handled only in PushNotificationTest.js

// Register Firebase messaging service worker with better error handling
const registerFirebaseMessagingServiceWorker = async () => {
  // Don't even attempt registration in iOS simulator
  if (isIOSSimulator()) {
    console.log('iOS Simulator detected - skipping Firebase service worker registration');
    return null;
  }

  if (!('serviceWorker' in navigator)) {
    console.log('Service Workers not supported in this browser');
    return null;
  }
  
  try {
    // Check for existing registration first
    const existingRegistrations = await navigator.serviceWorker.getRegistrations();
    
    for (const reg of existingRegistrations) {
      if (reg.active && reg.active.scriptURL.includes('firebase-messaging-sw.js')) {
        console.log('Firebase messaging service worker already registered:', reg.scope);
        return reg;
      }
    }
    
    // Only register if not already present
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
      updateViaCache: 'none' // Important for iOS
    });
    
    console.log('Firebase messaging service worker registered:', registration.scope);
    
    // Wait for activation on iOS if needed
    if (registration.installing) {
      await new Promise((resolve) => {
        const timeout = setTimeout(() => {
          console.log('Service worker activation timed out - continuing anyway');
          resolve();
        }, 5000);
        
        const stateChangeListener = (e) => {
          if (e.target && e.target.state === 'activated') {
            clearTimeout(timeout);
            if (registration.installing) {
              registration.installing.removeEventListener('statechange', stateChangeListener);
            }
            resolve();
          }
        };
        
        if (registration.installing) {
          registration.installing.addEventListener('statechange', stateChangeListener);
        } else {
          clearTimeout(timeout);
          resolve();
        }
      });
    }
    
    return registration;
  } catch (err) {
    console.error('Error registering firebase messaging service worker:', err);
    return null;
  }
};

// Initialize the application
const startApplication = async () => {
  // Check for iOS simulator first
  if (isIOSSimulator()) {
    console.log('iOS Simulator detected - some features may not work correctly');
  } else {
    // Only register Firebase SW if not in simulator
    await registerFirebaseMessagingServiceWorker();
  }
  
  // Render the React application
  ReactDOM.render(
    <React.StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </React.StrictMode>,
    document.getElementById('root')
  );
};

// Start the application
startApplication();


reportWebVitals();